import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { type MouseEvent, Suspense, memo, useState } from 'react';
import { type CarDataFields, InfoGridFuelCategory } from '../../_configs/types';
import { type ConfigInfoGridPrefixes } from '../../_tools/utils';
import { useRootContainerContext } from '../../context/root-container';
import { useSettingsContext } from '../../context/settings';
import { useResponsive } from '../../hooks/use-responsive';
import { SourceList } from '../../modules/_definition/types';
import checkUndefined from '../../utils/tools/checkUndefined';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import { baseUrl } from '../../vars';
import CarDetailsGrid from '../car-details-grid/component';
import CarPriceGrid from '../car-price-grid/component';
import CarPriceGridSkeleton from '../car-price-grid/skeleton';
import Image from '../image/image';
import CarTileListMiniSkeleton from './car-tile-list-mini-skeleton';
import { type CarTileListMiniProps } from './types';

function CarTileListMini(props: CarTileListMiniProps) {
    const { car, disabled: disabledProp, loading, module, onMiniClick, showMonthlyRate, sourceIndex, sourceList } = props;
    const rootContainer = useRootContainerContext();
    const settings = useSettingsContext();
    const xs = useResponsive(rootContainer, 'only', 'xs');
    const sm = useResponsive(rootContainer, 'only', 'sm');
    const xl = useResponsive(rootContainer, 'only', 'xl');

    const [clickCoord, setClickCoord] = useState<{ x: number; y: number }>(undefined);
    const [disabled, setDisabled] = useState<boolean>(false);

    const carImage = checkUndefinedNullOrEmpty(car) ? `${baseUrl}/media/placeholder.svg` : car?.bilder?.url?.[0];
    const carImageExternal = carImage?.includes('ars-ars.de') || carImage?.includes('/static/media/placeholder');

    const renderImages = (
        <Image
            alt={`Eyecatcher Fahrzeug ${car?.name}`}
            externalImage={carImageExternal}
            src={carImage}
            ratio="4/3"
            slotProps={{
                // @ts-expect-error - Type mismatch from styled
                img: { 'data-testid': `carTile_mini_${sourceList}_${sourceIndex}_Image` },
            }}
            sx={{ borderRadius: 0 }}
        />
    );

    const renderTexts = (
        <ListItemText
            primary={settings?.general?.useLocationAlias === true ? car?.loc?.firmaName : car?.loc?.name}
            secondary={car?.name || 'Fahrzeug wählen'}
            primaryTypographyProps={{
                'color': 'text.secondary',
                // @ts-ignore
                'data-testid': `carTile_mini_${sourceList}_${sourceIndex}_Title_Primary`,
                'variant': 'caption',
            }}
            secondaryTypographyProps={{
                'component': 'h2',
                'color': 'text.primary',
                // @ts-ignore
                'data-testid': `carTile_mini_${sourceList}_${sourceIndex}_Title_Secondary`,
                'sx': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: xs ? 2 : 1,
                    WebkitBoxOrient: 'vertical',
                },
                'variant': 'h6',
            }}
            sx={{
                [getStyleQuery()]: {
                    flexShrink: 0,
                    flexGrow: 0,
                },
            }}
        />
    );

    function handleMouseDown(e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) {
        if (!settings?.general?.mockup) {
            setClickCoord({ x: e?.clientX, y: e?.clientY });
            setDisabled(false);
        }
    }

    function handleMouseMove(e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) {
        if (!checkUndefinedNullOrEmpty(clickCoord) && !settings?.general?.mockup) {
            const allowedMovement = 20;

            if (Math.abs(clickCoord?.x - e?.clientX) < allowedMovement && Math.abs(clickCoord?.y - e?.clientY) < allowedMovement) {
                setDisabled(false);
            } else {
                setDisabled(true);
            }
        }
    }

    function handleMouseUp() {
        if (!settings?.general?.mockup) {
            setClickCoord(undefined);
        }
    }

    function handleOnClick(e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) {
        if (!settings.general.mockup && !disabled && !disabledProp && !checkUndefinedOrNull(onMiniClick)) {
            onMiniClick(e, car);
        }
    }

    const bigInfoGridArray: ConfigInfoGridPrefixes<CarDataFields>[] = !checkUndefined(settings?.cartile?.infoGrid?.big?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory])
        ? settings?.cartile?.infoGrid?.big?.[car?.kategorie]?.[car?.verbrauch?.fuelCategory]
        : !checkUndefined(settings?.cartile?.infoGrid?.big?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT])
          ? settings?.cartile?.infoGrid?.big?.[car?.kategorie]?.[InfoGridFuelCategory.DEFAULT]
          : !checkUndefined(settings?.cartile?.infoGrid?.big?.default?.[car?.verbrauch?.fuelCategory])
            ? settings?.cartile?.infoGrid?.big?.default?.[car?.verbrauch?.fuelCategory]
            : !checkUndefined(settings?.cartile?.infoGrid?.big?.default?.[InfoGridFuelCategory.DEFAULT])
              ? settings?.cartile?.infoGrid?.big?.default?.[InfoGridFuelCategory.DEFAULT]
              : [];

    const bigGrid: ConfigInfoGridPrefixes<CarDataFields>[] = bigInfoGridArray?.filter((i) => {
        const clearKey = i?.replace('[G]', '')?.replace('[L]', '') as CarDataFields;
        return !i.startsWith('[G]') && (clearKey !== 'verbrauch.text' || !checkUndefinedNullOrEmpty(car?.verbrauch?.combined));
    });

    return loading ? (
        <CarTileListMiniSkeleton rootContainer={rootContainer} settings={settings} />
    ) : (
        <Card
            className={[disabled || disabledProp ? '' : 'action', 'flat'].join(' ')}
            data-carid={car?.id}
            data-testid={`carTile_mini_${sourceList}_${sourceIndex}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onClick={handleOnClick}
            sx={{
                [getStyleQuery()]: {
                    color: 'text.primary',
                    cursor: disabled || disabledProp ? undefined : 'pointer',
                    m: 0,
                    p: 0,
                    textDecoration: 'none',
                    width: 1,
                },
            }}
        >
            <Grid container spacing={xs ? 0 : 3} alignItems="center" justifyContent="center" direction={xs ? 'column' : undefined}>
                <Grid size={{ xs: 6, sm: 2 }} sx={{ flexShrink: 0 }}>
                    {renderImages}
                </Grid>
                <Stack
                    component={Grid}
                    size={{ xs: 12, sm: 8 }}
                    gap={1.5}
                    divider={<Divider id="CarTile_Divider_3" orientation="horizontal" flexItem sx={{ [getStyleQuery()]: { borderStyle: 'dashed', mx: -3 } }} />}
                    sx={{ [getStyleQuery()]: xs ? { p: 1 } : { py: 1 } }}
                >
                    {renderTexts}
                    <CarDetailsGrid car={car} colsBig={{ xs: 4 }} colsList={{ xs: 1 }} mini smallFields={bigGrid?.slice(0, xl ? 6 : sm ? 4 : xs ? 2 : 5)} />
                    {(xs || sm) && (
                        <Suspense fallback={<CarPriceGridSkeleton showMonthlyRate={showMonthlyRate} smaller />}>
                            <CarPriceGrid
                                accessoiresPrice={props?.accessoiresPrice}
                                car={car}
                                carActive={false}
                                isProbefahrt={props?.isProbefahrt || false}
                                mini
                                module={module}
                                showMonthlyRate={showMonthlyRate}
                                smaller
                            />
                        </Suspense>
                    )}
                </Stack>
                {!(xs || sm) && (
                    <Grid size={2} sx={{ flexShrink: 0, pl: sourceList === SourceList.Contact ? 0 : undefined }}>
                        <Suspense fallback={<CarPriceGridSkeleton showMonthlyRate={showMonthlyRate} smaller />}>
                            <CarPriceGrid
                                accessoiresPrice={props?.accessoiresPrice}
                                car={car}
                                carActive={false}
                                isProbefahrt={props?.isProbefahrt || false}
                                mini
                                module={module}
                                showMonthlyRate={showMonthlyRate}
                                smaller
                                sourceList={sourceList}
                            />
                        </Suspense>
                    </Grid>
                )}
            </Grid>
        </Card>
    );
}

export default memo(CarTileListMini);
